import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import dayjs from "dayjs";
import PropTypes from "prop-types";

import IconButton from "../../../../packages/IconButton";
import WPHeading from "../../../../packages/WPHeading";
import WPRangePicker from "../../../../packages/WPRangePicker";

import { DATE_FORMATS } from "../../../../constants/global.constants";
import {
  DATE_RANGE_ACTIONS,
  LICENSE_SORT_DIRECTION,
} from "../../../../constants/LicenseManagement.constants";

import { CalendarIcon, SortIcon } from "../../../../assets/icons";

const LicenseLastLoginHeaderRenderer = ({
  displayName,
  sortDirection,
  onSortClick,
  onDateRangeChange,
  id,
  fromDate,
  toDate,
}) => {
  const { t } = useTranslation();

  const [showRangePicker, setShowRangePicker] = useState(false);
  const [rangeValue, setRangeValue] = useState([null, null]);
  const [appliedDateRange, setAppliedDateRange] = useState(null);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (fromDate && toDate) {
      setRangeValue([dayjs(fromDate), dayjs(toDate)]);
      setAppliedDateRange({ fromDate, toDate });
    } else if (!fromDate && !toDate) {
      setRangeValue([null, null]);
      setAppliedDateRange(null);
    }
  }, [fromDate, toDate]);

  const nextDirection =
    sortDirection === LICENSE_SORT_DIRECTION.ASC
      ? LICENSE_SORT_DIRECTION.DESC
      : LICENSE_SORT_DIRECTION.ASC;

  const handleSortClick = () => {
    onSortClick?.({ sortBy: "lastLogin", sortDirection: nextDirection });
  };

  const handleCalendarClick = () => {
    setShowRangePicker(true);
  };

  const handleDateRangeAction = (action) => {
    if (action === DATE_RANGE_ACTIONS.apply) {
      if (rangeValue?.[0] && rangeValue?.[1]) {
        const dateRange = {
          fromDate: dayjs(rangeValue[0]).format(DATE_FORMATS.YYYY_MM_DD),
          toDate: dayjs(rangeValue[1]).format(DATE_FORMATS.YYYY_MM_DD),
        };
        setShowRangePicker(false);
        setAppliedDateRange(dateRange);
        onDateRangeChange?.(dateRange);
      }
    } else if (action === DATE_RANGE_ACTIONS.clear) {
      setRangeValue([null, null]);
      setAppliedDateRange(null);
      onDateRangeChange?.(null);
      setShowRangePicker(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      const popup = pickerRef.current;
      if (popup && popup.contains(event.target)) return;
      if (
        document.querySelector(".ant-picker-dropdown")?.contains(event.target)
      )
        return;
      setShowRangePicker(false);
    }
    if (showRangePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showRangePicker]);

  const white = "var(--du-color-white)";
  const sortArrowFillConfig = {
    [LICENSE_SORT_DIRECTION.ASC]: { upFill: white, downFill: "none" },
    [LICENSE_SORT_DIRECTION.DESC]: { upFill: "none", downFill: white },
    default: { upFill: "none", downFill: "none" },
  };

  const sortIconProps = useMemo(() => {
    const { upFill, downFill } =
      sortArrowFillConfig[sortDirection] || sortArrowFillConfig.default;
    return {
      upColor: white,
      downColor: white,
      upFill,
      downFill,
    };
  }, [sortDirection]);

  const sortIcon = (
    <SortIcon
      style={{
        "--sort-up-color": sortIconProps.upColor,
        "--sort-down-color": sortIconProps.downColor,
        "--sort-up-fill": sortIconProps.upFill,
        "--sort-down-fill": sortIconProps.downFill,
      }}
    />
  );

  return (
    <div className="license-last-login-header-renderer" id={id}>
      <div className="last-login-sort-container">
        <WPHeading
          text={t(displayName)}
          size="small"
          type="default"
          color="var(--du-color-white)"
          className="last-login-header-text"
          id={`${id}-text`}
        />
        <IconButton
          id="last-login-sort-button"
          onClick={handleSortClick}
          tooltip={t("ValueStrings.sort")}
          placement="top"
          className="sort-button"
        >
          {sortIcon}
        </IconButton>
      </div>
      <div className="date-range-filter-container">
        <IconButton
          id="user-date-range-button"
          tooltip={t("ValueStrings.selectADateOrRange")}
          placement="top"
          className="date-range-filter-button"
          onClick={handleCalendarClick}
        >
          <div className="calendar-icon-container">
            <CalendarIcon fill={white} />
            {rangeValue[0] && rangeValue[1] && (
              <span
                className="license-filter-badge calendar-filter-badge"
                aria-label="Filter applied"
              />
            )}
          </div>
        </IconButton>
        {showRangePicker && (
          <div ref={pickerRef} className="license-date-range-picker-popup">
            <WPRangePicker
              value={rangeValue}
              onChange={setRangeValue}
              calendarPanel={true}
              showExtraFooter={true}
              onFooterBtnClick={handleDateRangeAction}
              disableClearBtn={rangeValue[0] === null && rangeValue[1] === null}
              disableApplyBtn={
                !rangeValue[0] || !rangeValue[1] || appliedDateRange !== null
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

LicenseLastLoginHeaderRenderer.propTypes = {
  displayName: PropTypes.string.isRequired,
  sortDirection: PropTypes.oneOf([
    ...Object.values(LICENSE_SORT_DIRECTION),
    null,
  ]),
  onSortClick: PropTypes.func,
  onDateRangeChange: PropTypes.func,
  id: PropTypes.string,
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
};

export default LicenseLastLoginHeaderRenderer;
